// src/context/documentUpload.js
//
// The document-upload rules shared by the Knowledge Base library (DocumentsView) and the personalization
// shared DocumentPicker (personalization form, persona assist forms) — PURE helpers, no React, no copy:
//   • the client-side file check (type + size), answered as a KEY so each caller words it;
//   • which picked names already exist (the backend REPLACES silently — the confirm is the caller's);
//   • the sequential upload loop: ONE request per file, and it STOPS at the first billing refusal (the
//     wallet is shared — every later file would be refused too);
//   • v197: the picker's rules (moved here from the personalization model so the persona forms share them).
// ⛔ No sentence here: each caller words problems and errors itself.
export const ALLOWED_EXT = ['pdf', 'docx', 'pptx', 'xlsx', 'txt', 'md'];
export const MAX_BYTES = 50 * 1024 * 1024;
export const ACCEPT = ALLOWED_EXT.map((e) => `.${e}`).join(',');

/** null when the file may be sent, else {key, params} under `context:documents.validation.*`. */
export function fileProblem(file) {
  const ext = (String(file?.name || '').split('.').pop() || '').toLowerCase();
  if (!ALLOWED_EXT.includes(ext)) {
    return { key: 'unsupportedType', params: { ext, exts: ALLOWED_EXT.join(', ').toUpperCase() } };
  }
  if (file.size > MAX_BYTES) return { key: 'tooLarge', params: {} };
  return null;
}

/** The picked names that already exist as stored (non-optimistic) documents. */
export function namesToReplace(files, docs) {
  return (files || [])
    .filter((f) => (docs || []).some((d) => d.filename === f.name && !d._optimistic))
    .map((f) => f.name);
}

/**
 * Upload `files` one at a time. `isRefusal(err)` → a truthy refusal object for a billing refusal (it stops the
 * loop: the rest are marked `skipped`). Callbacks let the caller keep its own optimistic rows.
 * @returns {Promise<Array<{file: File, ok: boolean, filename?: string, problem?: object, error?: Error,
 *   refusal?: object, skipped?: boolean}>>}
 */
export async function uploadSequentially({ files, upload, isRefusal, onStart, onSuccess, onFailure }) {
  const results = [];
  let refused = false;
  for (const file of files) {
    if (refused) { results.push({ file, ok: false, skipped: true }); continue; }
    const problem = fileProblem(file);
    if (problem) { results.push({ file, ok: false, problem }); continue; }
    if (onStart) onStart(file);
    try {
      const res = await upload(file);
      const filename = (res && res.filename) || file.name;
      results.push({ file, ok: true, filename });
      if (onSuccess) onSuccess(file, filename);
    } catch (error) {
      const refusal = isRefusal ? isRefusal(error) : null;
      if (onFailure) onFailure(file, error, refusal);
      if (refusal) refused = true;
      results.push({ file, ok: false, error, refusal: refusal || undefined });
    }
  }
  return results;
}

/** The most documents a request may list (personalization form, persona assist). */
export const MAX_LISTED_DOCUMENTS = 10;
/** The picker shows a filename search above this many rows. */
export const PICKER_SEARCH_THRESHOLD = 10;

/** The backend refuses a listed name with a path or a leading dot (the upload accepts the dot). */
export function isSelectableFilename(name) {
  return typeof name === 'string' && name.length > 0 && !name.startsWith('.')
    && !name.includes('/') && !name.includes('\\');
}

/** A KB row known to carry no extractable text (scanned PDF). Only a HINT: callers use their own extractor. */
export function isTextless(doc) {
  return doc?.status === 'ready' && doc?.chunk_count === 0;
}

/** This visit's uploads first (most recent first), then every other document ALPHABETICAL. Pure. */
export function orderPickerRows(docs, uploadedNow) {
  const recent = Array.isArray(uploadedNow) ? uploadedNow : [];
  const byName = new Map((docs || []).map((d) => [d.filename, d]));
  const top = recent.map((n) => byName.get(n)).filter(Boolean);
  const rest = (docs || [])
    .filter((d) => !recent.includes(d.filename))
    .slice()
    .sort((a, b) => a.filename.localeCompare(b.filename, 'it', { sensitivity: 'base' }));
  return [...top, ...rest];
}
